// Chantier : Boilerplate lot 4 (PLAN/audits/audit-boilerplate/BACKLOG.md)
// Regle metier : -
// Decision : -
// Labels/styles/helpers du module transports (camions inclus), extraits de
// lib/labels.ts (decontamination du socle, Axe 1).

import type { CategorieCamion } from '@/services/referentiels';
import type { PhaseTransport, StatutTransport, TypeContenuTransport, TypeOperationInerte, TypePrestation } from '@/services/transports';
import { STATUTS_TD_FINAUX, type Anomalie } from '@/types/bsd';

// Liste fermee des equipements possibles pour un types_camion. Utilisee dans
// le multi-select du formulaire admin et dans le filtre du wizard transport
// (Etape4VehiculeChargement). Pour ajouter un equipement : 1 ligne ici suffit.
export const EQUIPEMENTS_CAMION: { value: string; label: string }[] = [
  { value: 'debachable', label: 'Débâchable' },
  { value: 'hayon', label: 'Hayon' },
  { value: 'grue', label: 'Grue' },
  { value: 'chariot', label: 'Chariot' },
];

// Source unique des categories de vehicule. Le Record est exhaustif par le
// compilateur : ajouter une valeur a l'enum PG `categorie_camion` (et a l'union
// `CategorieCamion`) casse le typecheck tant qu'elle n'est pas libellee ici.
// L'ordre des cles est l'ordre d'affichage (du plus courant au plus rare, benne
// en dernier car elle porte le flux inerte) : `CATEGORIES_CAMION_OPTIONS` le
// reprend tel quel.
export const CATEGORIE_CAMION_LABELS: Record<CategorieCamion, string> = {
  porteur: 'Porteur',
  semi: 'Semi',
  camion_remorque: 'Camion remorque',
  plateau: 'Plateau',
  fourgon: 'Fourgon',
  vl: 'VL',
  benne: 'Benne',
};

// Options de select derivees du dictionnaire ci-dessus. Toute liste de
// categories proposee a l'utilisateur (referentiel admin, creation de
// transporteur, filtres du wizard) part d'ici : avant, 3 listes concurrentes
// coexistaient, dont 2 en dur et 1 tronquee a semi/porteur.
export const CATEGORIES_CAMION_OPTIONS: { value: CategorieCamion; label: string }[] =
  (Object.entries(CATEGORIE_CAMION_LABELS) as [CategorieCamion, string][])
    .map(([value, label]) => ({ value, label }));

// Capacite affichee au client, derivee de types_camion.nb_emplacements_pdc : un
// emplacement du plan de chargement = un big bag (arbitrage Neven 2026-09-05).
// 0 = pas de plan de chargement (benne, VL, fourgon) : rien a afficher plutot
// qu'un « 0 big bag » faux. Remplace le texte libre `capacite`, saisi en double
// et deja divergent en Prod (« 22-24 big bags » pour 22 emplacements).
export function formatCapaciteBigBags(nbEmplacementsPdc: number | null | undefined): string | null {
  if (!nbEmplacementsPdc || nbEmplacementsPdc <= 0) return null;
  return `${nbEmplacementsPdc} big bag${nbEmplacementsPdc > 1 ? 's' : ''}`;
}

// Predicat metier sur l'ENUM, jamais sur le libelle. Le bouton « Plan de chargement »
// client testait `dechetType.startsWith('Dangereux')` : il a disparu pour tous les
// clients le 2026-08-27, quand TYPE_CONTENU_LABELS est passe a « Suivi Trackdechet »
// (D06-59), sans qu'aucun test ne le voie. Un libelle se renomme ; un enum, non.
export function isContenuDangereux(typeContenu: TypeContenuTransport): boolean {
  return typeContenu === 'dangereux_connu' || typeContenu === 'dangereux_inconnu';
}

// RC3.86 (S98) : un camion de categorie `benne` n'a pas de plancher, donc pas de plan de
// chargement (PDF, rappel J-1, ecrans PDC). « Libre » (categorie null) le garde : l'admin
// n'a pas encore tranche, la grille par defaut s'applique.
export function transportSansPlanChargement(transport: { typeCamionCategorie?: string | null }): boolean {
  return transport.typeCamionCategorie === 'benne';
}

/**
 * Chantier : - (revue client n°3, RC3.133)
 * Regle metier : TR-R26 (PRODUIT/domaine/06_transport.md), matrice 15_autorisations.md
 * Decision : D-PDC-SAISIE-ADMIN (PRODUIT/decisions/)
 * Acces au plan de chargement, admin et client : transport dangereux hors benne, en phase commande ;
 * fige en traite (lecture seule), masque en annule (PDC supprime) et en chiffrage.
 */
export type AccesPlanChargement = 'aucun' | 'lecture' | 'saisie';

export function accesPlanChargement(transport: {
  typeContenu: TypeContenuTransport;
  typeCamionCategorie?: string | null;
  phase?: PhaseTransport | null;
  statut: StatutTransport;
}): AccesPlanChargement {
  if (!isContenuDangereux(transport.typeContenu) || transportSansPlanChargement(transport)) return 'aucun';
  if (transport.phase !== 'commande' || transport.statut === 'annule') return 'aucun';
  return transport.statut === 'traite' ? 'lecture' : 'saisie';
}

/**
 * Chantier : - (audit Prod du 2026-10-16, TR-2026-0110 et TR-2026-0016)
 * Regle metier : TR-R13 (PRODUIT/domaine/06_transport.md), TR-R11
 * Decision : D-CLOTURE-CONNU-SANS-BSD (PRODUIT/decisions/)
 * Bouton « Marquer Traité » d'un transport planifie : inconnu et materiel ; dangereux connu livre sans centre
 * (D-TRANSPORT-SUIVANT-01) ; dangereux connu dont aucun BSD n'est plus suivi, que la cloture automatique TR-R11 ne
 * fermera jamais : plus de BSD, ou chaque BSD termine (statut final TD, R1016D-2) ou inaccessible sur Trackdechets.
 * Un BSD absent de `bordereaux` (autre chantier d'un transport multi-chantiers) compte comme suivi (limite R1016D-7).
 */
export function peutMarquerTraite(
  transport: { statut: StatutTransport; typeContenu: TypeContenuTransport },
  livraisons: Array<{ centreId: string | null; marchandises: Array<{ bordereauId: string | null }> }>,
  bordereaux: Array<{ id: string; statut: string; anomalies: Anomalie[] }>,
): boolean {
  if (transport.statut !== 'planifie') return false;
  if (transport.typeContenu === 'dangereux_inconnu' || transport.typeContenu === 'materiel') return true;
  if (transport.typeContenu !== 'dangereux_connu') return false;
  if (livraisons.some((l) => l.centreId == null && l.marchandises.some((m) => !!m.bordereauId))) return true;
  const bsdIds = livraisons.flatMap((l) => l.marchandises.map((m) => m.bordereauId).filter((id): id is string => !!id));
  return bsdIds.every((id) => {
    const bsd = bordereaux.find((b) => b.id === id);
    if (!bsd) return false;
    return STATUTS_TD_FINAUX.includes(bsd.statut)
      || bsd.anomalies.some((a) => a.type === 'INACCESSIBLE_TD' && a.resolved_at == null);
  });
}

// Reference transport lisible. Fallback "brouillon <id8>" si la reference
// n'a pas encore ete generee (ex : transport brouillon).
export function formatTransportRef(reference: string | null | undefined, id: string): string {
  return reference || `brouillon ${id.slice(0, 8)}`;
}

// TR-R42, D06-67 : corriger un lieu de livraison ne change pas le depart des transports suivants (pas de propagation).
export function avertissementTransportsSuivants(suivants: Array<{ id: string; reference: string | null }>): string {
  const refs = suivants.map((t) => formatTransportRef(t.reference, t.id)).join(', ');
  return suivants.length > 1
    ? `Les transports suivants ${refs} partent de ce lieu : corrigez aussi leur départ.`
    : `Le transport suivant ${refs} part de ce lieu : corrigez aussi son départ.`;
}

/**
 * Decision : D06-57 (decisions_developpement.md)
 * Libelle "chargement camion" affiche a cote du type de camion. longueurPlancherM
 * null = camion complet ; valeur = chargement partiel (metres de plancher occupes).
 * Retourne null quand le concept ne s'applique pas : le type de camion n'a pas de
 * longueur de plancher (benne, fourgon, vl... ou aucun camion selectionne). Seuls
 * les camions a plancher (longueurPlancherMaxM renseignee) sont concernes.
 */
export function formatChargementCamion(opts: {
  longueurPlancherMaxM?: number | null;
  longueurPlancherM?: number | null;
  // D06-60 : camion « Libre (peu importe) ». longueurPlancherMaxM vient du join
  // types_camion : sans camion assigne elle est NULL, ce qui masquait le
  // complet/partiel declare par le client — precisement la donnee sur laquelle
  // SERME arbitre. Un flag explicite plutot qu'une garde assouplie : « Libre +
  // complet » (longueur NULL) est sinon indistinguable d'une benne.
  camionNonAssigne?: boolean;
}): string | null {
  if (opts.longueurPlancherMaxM == null && !opts.camionNonAssigne) return null;
  if (opts.longueurPlancherM == null) return 'Complet';
  return `Partiel : ${opts.longueurPlancherM} m de plancher`;
}

/**
 * Chantier : - (revue client RC2.61)
 * Decision : D06-57 (decisions_developpement.md)
 * Variante compacte de formatChargementCamion pour la carte kanban, ou la
 * colonne fait 300px : `Partiel (4.5m)` au lieu de `Partiel - 4.5 m de plancher`.
 * Les sidebars gardent le format long (arbitrage Neven 2026-08-30 : la place y
 * existe et le libelle explicite y est preferable).
 */
export function formatChargementCamionCourt(opts: {
  longueurPlancherMaxM?: number | null;
  longueurPlancherM?: number | null;
  /** D06-60 : camion « Libre (peu importe) ». Cf. formatChargementCamion. */
  camionNonAssigne?: boolean;
}): string | null {
  if (opts.longueurPlancherMaxM == null && !opts.camionNonAssigne) return null;
  if (opts.longueurPlancherM == null) return 'Complet';
  return `Partiel (${opts.longueurPlancherM}m)`;
}

/**
 * Chantier : - (revue client RC2.57)
 * Regle metier : TR-R03 (PRODUIT/domaine/06_transport.md)
 * Decision : D06-60 (decisions_developpement.md)
 * Libelle ecran d'un type de camion. Un type absent n'est pas une donnee
 * manquante : sur les flux dangereux et materiel, il signifie que le client a
 * coche « Libre (peu importe) » et que l'arbitrage revient a SERME. Sans ce
 * helper les ecrans rendaient un blanc ou un tiret, indistinguables d'un bug.
 * Reserve a l'affichage : les documents transporteur (PDF d'affretement, emails)
 * passent par formatTypeCamionAvecChargement, ou l'absence reste une chaine vide.
 */
export function labelTypeCamion(nom: string | null | undefined): string {
  return nom || 'Libre (peu importe)';
}

/**
 * Decision : D-UX-DATES-TRANSPORT (decisions_developpement.md, amendement inerte 2026-09-02)
 * Vocabulaire des deux dates planifiees/souhaitees selon le flux. En inerte, la date
 * de chargement est le passage du camion chez le client (les 4 operations TR-R28,
 * depose comprise) et la date de livraison est l'arrivee au centre. Memes colonnes
 * (`date_chargement_effective` / `date_livraison_effective`), mots differents.
 * Source unique pour toutes les surfaces a type de contenu connu ; la grille
 * /operations (colonnes mixtes tous flux) garde « Chargement » / « Livraison ».
 */
export function labelsDatesTransport(typeContenu: string | null | undefined): { chargement: string; livraison: string } {
  return typeContenu === 'inerte'
    ? { chargement: 'Passage', livraison: 'Arrivée au centre' }
    : { chargement: 'Chargement', livraison: 'Livraison' };
}

/**
 * Decision : D06-57 (decisions_developpement.md)
 * Type de camion + chargement pour les variables PDF (Fiche Transport, Confirmation
 * d'affretement). Reutilise formatChargementCamion. Renvoie le nom seul si pas de
 * plancher (benne, fourgon... ou aucun camion).
 */
export function formatTypeCamionAvecChargement(
  nom: string | null | undefined,
  opts: { longueurPlancherMaxM?: number | null; longueurPlancherM?: number | null },
): string {
  if (!nom) return '';
  const chargement = formatChargementCamion(opts);
  return chargement ? `${nom} · ${chargement}` : nom;
}

// Libelles alignes sur PRODUIT/domaine/enumerations.md type_operation_inerte
// (libelle admin = libelle client = libelle court). Les precisions techniques
// (mouvement physique de la benne) restent dans la doc, pas dans l'UI/PDF.
export const TYPE_OPERATION_INERTE_LABELS: Record<TypeOperationInerte, string> = {
  depose: 'Dépose',
  rotation: 'Rotation',
  enlevement: 'Enlèvement',
  chargement_immediat: 'Chargement immédiat',
};

export const TYPE_PRESTATION_LABELS: Record<TypePrestation, string> = {
  location: 'Location',
  materiel: 'Équipement',
  indexation_gasoil: 'Indexation gasoil',
};

export const STATUT_TRANSPORT_LABELS: Record<StatutTransport, string> = {
  en_attente_chiffrage: 'En cours de chiffrage',
  chiffre: 'Chiffré',
  brouillon: 'Brouillon',
  a_qualifier: 'À qualifier',
  qualifie: 'Qualifié',
  planifie: 'Planifié',
  traite: 'Traité',
  annule: 'Annulé',
};

// Labels simplifies pour les pages client (a_qualifier + qualifie = "En cours de traitement")
export const STATUT_TRANSPORT_LABELS_CLIENT: Record<StatutTransport, string> = {
  en_attente_chiffrage: 'En cours de chiffrage',
  chiffre: 'Chiffré',
  brouillon: 'Brouillon',
  a_qualifier: 'En cours de traitement',
  qualifie: 'En cours de traitement',
  planifie: 'Planifié',
  traite: 'Traité',
  annule: 'Annulé',
};

// `dot` : pastille des grilles admin (/operations), meme couleur que le badge.
export const STATUT_TRANSPORT_CLIENT_STYLE: Record<StatutTransport, { badge: string; border: string; dot: string }> = {
  en_attente_chiffrage: { badge: 'bg-orange-500 text-white', border: 'border-l-orange-500', dot: 'bg-orange-500' },
  chiffre:              { badge: 'bg-green-600 text-white',  border: 'border-l-green-600',  dot: 'bg-green-600'  },
  brouillon:            { badge: 'bg-gray-400 text-white',   border: 'border-l-gray-400',   dot: 'bg-gray-400'   },
  a_qualifier:          { badge: 'bg-amber-500 text-white', border: 'border-l-amber-500', dot: 'bg-amber-500' },
  qualifie:             { badge: 'bg-amber-500 text-white', border: 'border-l-amber-500', dot: 'bg-amber-500' },
  planifie:             { badge: 'bg-blue-600 text-white',   border: 'border-l-blue-600',   dot: 'bg-blue-600'   },
  traite:               { badge: 'bg-green-700 text-white',  border: 'border-l-green-700',  dot: 'bg-green-700'  },
  annule:               { badge: 'bg-red-600 text-white',    border: 'border-l-red-600',    dot: 'bg-red-600'    },
};

// STATUT_TRANSPORT_COLORS_CLIENT et STATUT_TRANSPORT_COLORS supprimes 2026-05-20
// (D-UX-LABELS-SIMPLIFY) : 3 mappings pour la meme donnee = risque de divergence.
// STATUT_TRANSPORT_CLIENT_STYLE[s].badge est desormais la seule source de verite
// (les valeurs des 2 constantes supprimees etaient strictement identiques aux badges
// du STYLE).



// Options de filtre statut client (avec groupement a_qualifier+qualifie)
export const TRANSPORT_STATUS_FILTER_OPTIONS_CHIFFRAGE = ['en_attente_chiffrage', 'chiffre', 'annule'] as const;
export const TRANSPORT_STATUS_FILTER_OPTIONS_COMMANDE = ['brouillon', 'en_cours_traitement', 'planifie', 'traite', 'annule'] as const;
// Les options de filtre sont separees par phase : utiliser CHIFFRAGE ou COMMANDE directement

export const TRANSPORT_STATUS_FILTER_LABELS: Record<string, string> = {
  en_attente_chiffrage: 'En cours de chiffrage',
  chiffre: 'Chiffré',
  brouillon: 'Brouillon',
  en_cours_traitement: 'En cours de traitement',
  planifie: 'Planifié',
  traite: 'Traité',
  annule: 'Annulé',
};

export const TRANSPORT_STATUS_FILTER_MAP: Record<string, StatutTransport[]> = {
  en_cours_traitement: ['a_qualifier', 'qualifie'],
};

// Source unique : ecrans ET documents Carbone (confirmation d'affretement). Le dictionnaire
// TYPE_CONTENU_CONFIRMATION_LABELS qui doublonnait dans affretement.ts a ete supprime, les
// deux vocabulaires ayant fini par diverger sans raison (« Dangereux (BSD) » vs « Colis BSD »).
export const TYPE_CONTENU_LABELS: Record<TypeContenuTransport, string> = {
  dangereux_connu: 'Suivi Trackdéchet',
  dangereux_inconnu: 'Suivi Trackdéchet (ext)',
  inerte: 'Déchet inerte',
  // RC2.43 : « Materiel » -> « Marchandises generales » (revue client 2026-08-25). La cle
  // d'enum `materiel` (type_marchandise) et la colonne nom_materiel ne changent pas.
  materiel: 'Marchandises générales',
};

export const TYPE_CONTENU_COLORS: Record<TypeContenuTransport, string> = {
  dangereux_connu: 'bg-red-100 text-red-800',
  dangereux_inconnu: 'bg-orange-100 text-orange-800',
  inerte: 'bg-emerald-100 text-emerald-800',
  materiel: 'bg-blue-100 text-blue-800',
};

// Formate descriptionLibre pour l'affichage.
// Refonte 2026-04 : le champ ne contient plus que du texte libre. Les anciens
// transports peuvent contenir du JSON serialise (inerte / dangereux_inconnu) :
// on les ignore (les vraies donnees sont dans la table marchandises).
export function formatDescriptionLibre(raw: string | null | undefined): string | null {
  if (!raw) return null;
  // Legacy : si ressemble a du JSON, ne pas l'afficher (les marchandises sont
  // affichees ailleurs depuis les colonnes structurees).
  if (raw.trimStart().startsWith('[')) return null;
  return raw;
}
